import { useState, useEffect, useMemo } from 'react';
import { Menu, X, ArrowRight, PawPrint, Sparkles, Home } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence, useScroll } from 'motion/react';

export function Navigation() {
    const currentPath = window.location.pathname;
    const isAnimalServicesPage = currentPath === '/webdesign-tierdienstleister';

    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeHref, setActiveHref] = useState(
        isAnimalServicesPage ? '#tierprojekte' : '#ueber-mich'
    );

    const { scrollYProgress } = useScroll();

    const navLinks = useMemo(() => {
        if (isAnimalServicesPage) {
            return [
                {
                    label: 'Startseite',
                    href: '/',
                    icon: Home,
                },
                {
                    label: 'Beispiele',
                    href: '#tierprojekte',
                    isHighlight: true,
                },
                {
                    label: 'Leistungen',
                    href: '#leistungen-tierdienstleister',
                },
                {
                    label: 'Ablauf',
                    href: '#ablauf-tierdienstleister',
                },
            ];
        }

        return [
            {
                label: 'Für Tierdienstleister',
                href: '/webdesign-tierdienstleister',
                isHighlight: true,
            },
            { label: 'Leistungen', href: '#dienstleistungen' },
            { label: 'Projekte', href: '#projekte' },
            { label: 'Ablauf', href: '#ablauf' },
            { label: 'FAQ', href: '#faq' },
        ];
    }, [isAnimalServicesPage]);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 12);
        handleScroll();

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Active section highlight
    useEffect(() => {
        const ids = navLinks
            .filter((link) => link.href.startsWith('#'))
            .map((link) => link.href.replace('#', ''));

        const elements = ids
            .map((id) => document.getElementById(id))
            .filter(Boolean);

        if (!elements.length) return;

        const ratios = new Map();

        const pickBest = () => {
            let bestId = null;
            let bestRatio = 0;

            for (const [id, ratio] of ratios.entries()) {
                if (ratio > bestRatio) {
                    bestRatio = ratio;
                    bestId = id;
                }
            }

            if (!bestId) {
                const y = 120;
                let closest = null;
                let closestDist = Infinity;

                for (const el of elements) {
                    const top = el.getBoundingClientRect().top;
                    const dist = Math.abs(top - y);

                    if (dist < closestDist) {
                        closestDist = dist;
                        closest = el.id;
                    }
                }

                bestId = closest;
            }

            if (bestId) setActiveHref(`#${bestId}`);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    ratios.set(
                        entry.target.id,
                        entry.isIntersecting ? entry.intersectionRatio : 0
                    );
                }

                pickBest();
            },
            {
                root: null,
                rootMargin: '-15% 0px -65% 0px',
                threshold: [0, 0.1, 0.25, 0.4, 0.6, 0.8, 1],
            }
        );

        elements.forEach((el) => observer.observe(el));

        requestAnimationFrame(() => {
            elements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const inView = rect.top < window.innerHeight && rect.bottom > 0;
                ratios.set(el.id, inView ? 0.0001 : 0);
            });

            pickBest();
        });

        return () => observer.disconnect();
    }, [navLinks]);

    const handleNavClick = (href) => {
        setIsMobileMenuOpen(false);
        setActiveHref(href);

        // Seitenroute, z.B. / oder /webdesign-tierdienstleister
        if (href.startsWith('/')) {
            window.location.href = href;
            return;
        }

        // Anchor-Link, z.B. #kontakt
        if (!href.startsWith('#')) return;

        const el = document.querySelector(href);

        // Fallback: Wenn Anchor auf aktueller Seite nicht existiert, zur Startseite mit Anchor
        if (!el) {
            window.location.href = `/${href}`;
            return;
        }

        const headerOffset = 90;
        const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;

        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    const handleLogoClick = (e) => {
        e.preventDefault();
        setIsMobileMenuOpen(false);

        if (window.location.pathname === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        window.location.href = '/';
    };

    const logoSrc = `${process.env.PUBLIC_URL}/assets/LogoJITS.svg`;

    return (
        <>
            {/* Top progress bar */}
            <motion.div
                className="fixed top-0 left-0 right-0 z-[60] h-[2px] origin-left bg-gradient-to-r from-amber-500 via-orange-500 to-purple-600"
                style={{ scaleX: scrollYProgress }}
            />

            <motion.nav
                className="fixed top-0 left-0 right-0 z-50"
                initial={{ y: -90, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.55, ease: 'easeOut' }}
            >
                <div
                    className={`mx-auto max-w-7xl px-4 pt-3 ${
                        isScrolled ? 'pb-2' : 'pb-3'
                    } transition-all duration-300`}
                >
                    <div
                        className={`relative rounded-2xl border transition-all duration-300 ${
                            isScrolled
                                ? 'bg-white/90 backdrop-blur-xl shadow-lg border-black/10'
                                : 'bg-white/65 backdrop-blur-md shadow-sm border-black/5'
                        }`}
                    >
                        {/* Glow */}
                        <div className="pointer-events-none absolute -inset-px rounded-2xl opacity-45 blur-xl bg-gradient-to-r from-amber-500/25 via-orange-500/20 to-purple-600/20" />

                        <div className="relative flex items-center justify-between px-4 py-3 md:px-5">
                            {/* Brand + Logo */}
                            <motion.a
                                href="/"
                                onClick={handleLogoClick}
                                className="group flex items-center gap-3"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <div className="relative h-10 w-10 rounded-xl bg-white/75 border border-black/10 shadow-sm overflow-hidden flex items-center justify-center">
                                    <img
                                        src={logoSrc}
                                        alt="Johannsen IT Logo"
                                        className="h-25 w-25"
                                        draggable="false"
                                    />

                                    <div className="absolute inset-0 rounded-xl opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-60 bg-gradient-to-br from-amber-500 via-orange-500 to-purple-600" />
                                </div>

                                <div className="leading-tight">
                                    <div className="flex items-center gap-2">
                                        <div className="text-base md:text-lg font-semibold tracking-tight text-gray-900">
                                            Johannsen IT Solutions
                                        </div>

                                        <span className="hidden lg:inline-flex items-center gap-1 rounded-full border border-amber-500/20 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                            <PawPrint className="h-3 w-3" />
                                            Tierbranche
                                        </span>
                                    </div>

                                    <div className="text-xs text-gray-600 -mt-0.5">
                                        Websites für Hundefriseure & Tierdienstleister
                                    </div>
                                </div>
                            </motion.a>

                            {/* Desktop */}
                            <div className="hidden md:flex items-center gap-3">
                                <div className="relative flex items-center rounded-full border border-black/10 bg-white/75 backdrop-blur px-1.5 py-1">
                                    {navLinks.map((link) => {
                                        const isActive = activeHref === link.href;
                                        const Icon = link.icon;

                                        return (
                                            <button
                                                key={link.href}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleNavClick(link.href);
                                                }}
                                                className={`relative flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-colors ${
                                                    link.isHighlight
                                                        ? isActive
                                                            ? 'text-amber-800'
                                                            : 'text-amber-700 hover:text-amber-900'
                                                        : isActive
                                                            ? 'text-gray-900'
                                                            : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                {isActive && (
                                                    <motion.span
                                                        layoutId="nav-pill"
                                                        className={`absolute inset-0 rounded-full ${
                                                            link.isHighlight
                                                                ? 'bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-purple-600/15'
                                                                : 'bg-gradient-to-r from-blue-600/15 via-purple-600/15 to-pink-600/15'
                                                        }`}
                                                        transition={{
                                                            type: 'spring',
                                                            stiffness: 420,
                                                            damping: 32,
                                                        }}
                                                    />
                                                )}

                                                <span className="relative flex items-center gap-1.5">
                                                    {Icon && <Icon className="h-3.5 w-3.5" />}
                                                    {link.isHighlight && !Icon && (
                                                        <PawPrint className="h-3.5 w-3.5" />
                                                    )}
                                                    {link.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                                    <Button
                                        onClick={() => handleNavClick('#kontakt')}
                                        className="group bg-gradient-to-r from-amber-500 via-orange-500 to-purple-600 hover:from-amber-600 hover:via-orange-600 hover:to-purple-700 shadow-md text-white"
                                    >
                                        <span className="text-white">
                                            Kostenlosen Website-Check
                                        </span>
                                        <ArrowRight className="ml-2 h-4 w-4 text-white transition-transform group-hover:translate-x-0.5" />
                                    </Button>
                                </motion.div>
                            </div>

                            {/* Mobile menu button */}
                            <div className="md:hidden">
                                <motion.button
                                    className="p-2 rounded-xl border border-black/10 bg-white/75 backdrop-blur shadow-sm"
                                    onClick={() => setIsMobileMenuOpen((s) => !s)}
                                    aria-label="Toggle menu"
                                    whileTap={{ scale: 0.94 }}
                                >
                                    <AnimatePresence mode="wait">
                                        {isMobileMenuOpen ? (
                                            <motion.div
                                                key="close"
                                                initial={{ rotate: -90, opacity: 0 }}
                                                animate={{ rotate: 0, opacity: 1 }}
                                                exit={{ rotate: 90, opacity: 0 }}
                                                transition={{ duration: 0.18 }}
                                            >
                                                <X className="w-6 h-6 text-gray-900" />
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="menu"
                                                initial={{ rotate: 90, opacity: 0 }}
                                                animate={{ rotate: 0, opacity: 1 }}
                                                exit={{ rotate: -90, opacity: 0 }}
                                                transition={{ duration: 0.18 }}
                                            >
                                                <Menu className="w-6 h-6 text-gray-900" />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.nav>

            {/* Mobile drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsMobileMenuOpen(false)}
                        />

                        {/* Right drawer */}
                        <motion.aside
                            className="fixed top-0 right-0 z-[80] h-dvh w-[92vw] max-w-sm"
                            initial={{ x: '110%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '110%' }}
                            transition={{ type: 'spring', stiffness: 360, damping: 34 }}
                        >
                            <div className="relative h-full overflow-hidden border-l border-white/10 bg-white">
                                {/* Gradient header */}
                                <div className="relative px-5 pt-5 pb-5 bg-gradient-to-br from-amber-500 via-orange-500 to-purple-600 text-white">
                                    <div className="absolute inset-0 opacity-35 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.45),transparent_55%)]" />

                                    <div className="relative flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="h-11 w-11 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center overflow-hidden">
                                                <img
                                                    src={logoSrc}
                                                    alt="Johannsen IT Logo"
                                                    className="h-25 w-25"
                                                    draggable="false"
                                                />
                                            </div>

                                            <div>
                                                <div className="text-xl font-semibold leading-tight">
                                                    Johannsen IT Solutions
                                                </div>
                                                <div className="text-sm text-white/85 leading-tight">
                                                    Websites für Tierdienstleister
                                                </div>
                                            </div>
                                        </div>

                                        <motion.button
                                            className="h-11 w-11 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            aria-label="Close menu"
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <X className="h-6 w-6" />
                                        </motion.button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="px-4 py-4 h-[calc(100%-220px)] overflow-y-auto">
                                    <div className="grid gap-2">
                                        {navLinks.map((link, index) => {
                                            const isActive = activeHref === link.href;
                                            const Icon = link.icon;

                                            return (
                                                <motion.button
                                                    key={link.href}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleNavClick(link.href);
                                                    }}
                                                    className={`w-full text-left px-4 py-3 rounded-2xl border transition-colors ${
                                                        isActive
                                                            ? link.isHighlight
                                                                ? 'border-amber-500/20 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-purple-600/10 text-gray-900'
                                                                : 'border-black/10 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 text-gray-900'
                                                            : 'border-black/5 bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900'
                                                    }`}
                                                    initial={{ opacity: 0, x: 18 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.04 * index, duration: 0.18 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2 text-sm font-medium">
                                                            {Icon && <Icon className="h-4 w-4 text-amber-600" />}
                                                            {link.isHighlight && !Icon && (
                                                                <PawPrint className="h-4 w-4 text-amber-600" />
                                                            )}
                                                            {link.label}
                                                        </div>

                                                        <ArrowRight className="h-4 w-4 opacity-60" />
                                                    </div>
                                                </motion.button>
                                            );
                                        })}
                                    </div>

                                    <div className="mt-4 rounded-2xl border border-amber-500/15 bg-amber-50 p-4">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                            <Sparkles className="h-4 w-4 text-amber-600" />
                                            Kostenloser Website-Check
                                        </div>

                                        <div className="text-sm text-gray-600 mt-1">
                                            Ich prüfe Website, Google-Sichtbarkeit und Anfrageweg deines
                                            Tierbusiness und zeige dir konkrete Verbesserungen.
                                        </div>

                                        <Button
                                            onClick={() => handleNavClick('#kontakt')}
                                            className="mt-3 w-full group bg-gradient-to-r from-amber-500 via-orange-500 to-purple-600 hover:from-amber-600 hover:via-orange-600 hover:to-purple-700 shadow-md text-white"
                                        >
                                            <span className="text-white">Check anfragen</span>
                                            <ArrowRight className="ml-2 h-4 w-4 text-white transition-transform group-hover:translate-x-0.5" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Bottom sticky bar */}
                                <div className="px-4 py-4 border-t border-black/5 bg-white">
                                    <div className="text-xs text-gray-500">
                                        Johannsen IT Solutions • Websites für Hundefriseure & Tierdienstleister
                                    </div>
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}